/* eslint-disable */

import React, {useState, useEffect} from 'react'
import {Global, css} from '@emotion/react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useLocation} from 'react-router-dom'

import {
  FileSpreadsheet,
  Loader2,
  Filter,
  ArrowUpDown,
  ArrowDown,
  ArrowUp,
  Download,
  Building2,
  HelpCircle,
} from 'lucide-react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table'
import Select from 'react-select'
import 'react-datepicker/dist/react-datepicker.css'
import {Modal} from 'react-bootstrap'
import FilterModal from './FilterModal'
import DownloadModal from './DownloadModal'
import {StatsCard} from './StatsCard'
import * as API from '../../../api'
import styled from '@emotion/styled'
import ReportsData from './ReportsData'
import {HelpModal} from './HelpModal'
import { isSuperAdmin } from '../../../modules/auth/core/Auth';

interface ScreenProps {
  report: string
  title?: string
  reportMethod?: string
}

const StyledWrapper = styled.div`
  .filter-modal-dialog {
    width: 900px;
    max-width: 90vw;
  }
`

const REPLACE_COLUMNS_NAME = [
  ['IDPedido', 'Pedido'],
  ['DataAprovado', 'Data Aprovado'],
  ['DataFaturado', 'Data Faturado'],
  ['DataEntregue', 'Data Entregue'],
  ['DataCancelado', 'Data Cancelado'],
  ['DataApurado', 'Data Apurado'],
  ['CNPJLojista', 'CNPJ'],
  ['RazaoSocial', 'Razão Social'],
  ['Industria', 'Indústria'],
  ['IDSku', 'SKU'],
  ['FamiliaProduto', 'Familia Produto'],
  ['GrupoProduto', 'Grupo Produto'],
  ['QuantidadeFaturada', 'Quantidade Faturada'],
  ['PrecoTotalSemDesconto', 'Preço Total Sem Desconto'],
  ['PrecoTotalComDesconto', 'Preço Total Com Desconto'],
  ['ValorFaturado', 'Valor Faturado'],
  ['ValorCorte', 'Valor Corte'],
  ['OrigemPedido', 'Origem Pedido'],
  ['TipoVenda', 'Tipo Venda'],
  ['MeioPagamento', 'Meio de Pagamento'],
  ['PrazoPagamento', 'Prazo de Pagamento'],
  ['NomeVendedor', 'Nome Vendedor'],
  ['Data', 'Data Captado'],
  ['DataOcorrenciaFaturado', 'Data Ocorrência Faturado'],
]

function getStringDate(date: any) {
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const dayFormatted = day < 10 ? '0' + day : day.toString()
  const monthFormatted = month < 10 ? '0' + month : month.toString()
  return `${year}-${monthFormatted}-${dayFormatted}`
}

function getColumnHeader(colName: any) {
  const found = REPLACE_COLUMNS_NAME.find(([original]) => original === colName)
  if (found) return found[1]
  return colName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s: any) => s.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim()
}

function extractFileNameFromUrl(urlStr: string) {
  try {
    const url = new URL(urlStr)
    const last = url.pathname.split('/').filter(Boolean).pop()
    return last || 'manual.pdf'
  } catch {
    const last = urlStr.split('/').filter(Boolean).pop()
    return last || 'manual.pdf'
  }
}

export const ReportSystem: React.FC<ScreenProps> = ({report = '', title = '', reportMethod = ''}) => {
  const [data, setData] = useState<any[]>([])
  const [columns, setColumns] = useState<any[]>([])
  const [sorting, setSorting] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState<any>(null)
  const [showFilterModal, setShowFilterModal] = useState(true)
  const [limit, setLimit] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const columnHelper = createColumnHelper<any>()

  const location = useLocation()

  const [downloadError, setDownloadError] = useState(false)
  const [downloadURL, setDownloadURL] = useState<any>('')
  const [downloadModal, showDownloadModal] = useState<boolean>(false)
  const [jobId, setJobId] = useState<any>(null)

  const [showHelpModal, setShowHelpModal] = useState(false)
  const [helpPdfUrl, setHelpPdfUrl] = useState<string | null>(null)
  const [helpFileName, setHelpFileName] = useState<string | null>(null)
  const [hasPdf, setHasPdf] = useState(false)
  const [isCheckingManual, setIsCheckingManual] = useState(false)

  const checkManual = async () => {
    setIsCheckingManual(true)
    try {
      const response: any = await API.getReportManual(report)

      const src = response?.success ? response?.data?.src : null
      if (src) {
        setHasPdf(true)
        setHelpPdfUrl(src)
        setHelpFileName(extractFileNameFromUrl(src))
      } else {
        setHasPdf(false)
        setHelpPdfUrl(null)
        setHelpFileName(null)
      }
    } catch (error) {
      console.error('Error checking manual:', error)
      setHasPdf(false)
      setHelpPdfUrl(null)
      setHelpFileName(null)
    } finally {
      setIsCheckingManual(false)
    }
  }

  useEffect(() => {
    // Sempre que mudar a rota / tipo de relatório: reset + checa manual do relatório
    setFilters(null)
    setData([])
    setColumns([])
    setTotalCount(0)
    setSorting([])
    setShowFilterModal(true)
    setIsLoading(false)

    if (report) {
      checkManual()
    } else {
      setHasPdf(false)
      setHelpPdfUrl(null)
      setHelpFileName(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, report])

  useEffect(() => {
    // Só carrega dados se já tiver filtros
    if (!filters) return
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, filters, location.pathname])

  const loadData = async () => {
    if (!filters) return
    setIsLoading(true)

    const filtersResponse: any = ReportsData[report as keyof typeof ReportsData]
    const hasDate = filtersResponse.find((f: any) => {
      return f.name == 'dtstart'
    })

    try {
      const response = await (API as any)[report == 'infrapay' ? 'infrapayReport' : 'getIexpReport'](
        filters ||
          (hasDate
            ? {
                dtstart: getStringDate(new Date(new Date().setDate(new Date().getDate() - 7))),
                dtend: getStringDate(new Date()),
              }
            : {}),
        report,
        false,
        limit
      )

      const formattedColumns = formatColumns(response.columns)
      const formattedData = response.data

      setColumns(formattedColumns)
      setData(formattedData)
      setTotalCount(response.counter)
    } catch (error) {
      console.error('Error loading data:', error)
      setColumns([])
      setData([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }

  const formatColumns = (cols: any) => {
    return cols.map((col: any, idx: any) => {
      const displayName = getColumnHeader(col)
      return columnHelper.accessor(idx.toString(), {
        header: displayName,
        cell: (info: any) => info.getValue(),
        enableSorting: true,
      })
    })
  }

  const table = useReactTable({
    data,
    columns,
    state: {sorting},
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  async function exportXLSX() {
    setDownloadError(false)
    showDownloadModal(true)
    if (totalCount > 2000000) return

    const response = await API[report == 'infrapay' ? 'infrapayReport' : 'getIexpReport'](
      filters || {
        dtstart: getStringDate(new Date(new Date().setDate(new Date().getDate() - 7))),
        dtend: getStringDate(new Date()),
      },
      report,
      true,
      limit
    )

    if (!response || !response.success) {
      setDownloadError(true)
    } else {
      setJobId(response.data)
    }
  }

  return (
    <StyledWrapper>
      <Global
        styles={css`
          .filter-modal-dialog {
            width: 800px !important;
            max-width: 80vw;
          }
        `}
      />
      <div className='min-h-screen bg-gray-50 py-8'>
        <div className='mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='mb-8'>
            <div className='flex items-center justify-between'>
              <PageTitle>{title}</PageTitle>
              <div className='flex items-center gap-4'>
                <Select
                  className='w-32'
                  value={{value: limit, label: limit}}
                  onChange={(option: any) => setLimit(option.value)}
                  options={[
                    {value: 100, label: 100},
                    {value: 250, label: 250},
                    {value: 500, label: 500},
                    {value: 1000, label: 1000},
                  ]}
                />
                <button
                  onClick={() => setShowFilterModal(true)}
                  className='inline-flex items-center px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors'
                >
                  <Filter className='w-5 h-5 mr-2' />
                  Filtros
                </button>
                <button
                  onClick={() => exportXLSX()}
                  className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                >
                  <Download className='w-5 h-5 mr-2' />
                  Exportar XLSX
                </button>
                { !isCheckingManual && (hasPdf || isSuperAdmin()) ? 
                <button
                  onClick={() => setShowHelpModal(true)}
                  disabled={isCheckingManual}
                  className={`inline-flex items-center p-2 rounded-lg transition-all shadow-sm ${
                    hasPdf ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                  } ${isCheckingManual ? 'opacity-60 cursor-not-allowed' : ''}`}
                  title={
                    isCheckingManual
                      ? 'Verificando manual...'
                      : hasPdf
                        ? 'Ver manual de ajuda'
                        : 'Adicionar manual de ajuda'
                  }
                >
                  <HelpCircle className='w-5 h-5' />
                </button> : null }
              </div>
            </div>

            {!isLoading && filters && (
              <div className='mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                <StatsCard
                  title='Total de Resultados'
                  value={totalCount}
                  description='Total de registros na base'
                  icon={<FileSpreadsheet className='w-6 h-6' />}
                />
                <StatsCard
                  title='Resultados Visíveis'
                  value={data.length}
                  description={`Mostrando ${Math.min(limit, data.length)} registros`}
                  icon={<Building2 className='w-6 h-6' />}
                />
              </div>
            )}
          </div>

          <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
            {isLoading ? (
              <div className='flex flex-col items-center justify-center py-12'>
                <Loader2 className='w-8 h-8 text-blue-500 animate-spin' />
                <p className='mt-4 text-gray-500'>Carregando dados...</p>
              </div>
            ) : !filters ? (
              <div className='flex flex-col items-center justify-center py-12'>
                <div className='bg-gray-100 rounded-full p-3'>
                  <Filter className='w-8 h-8 text-gray-400' />
                </div>
                <p className='mt-4 text-gray-600'>Defina os filtros iniciais para visualizar os dados do relatório.</p>
                <button
                  onClick={() => setShowFilterModal(true)}
                  className='mt-4 inline-flex items-center px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors'
                >
                  <Filter className='w-5 h-5 mr-2' />
                  Abrir filtros
                </button>
              </div>
            ) : !data.length ? (
              <div className='flex flex-col items-center justify-center py-12'>
                <div className='bg-gray-100 rounded-full p-3'>
                  <FileSpreadsheet className='w-8 h-8 text-gray-400' />
                </div>
                <p className='mt-4 text-gray-500'>Nenhum registro encontrado com os filtros selecionados</p>
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    {table.getHeaderGroups().map((headerGroup: any) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header: any) => {
                          const sorted = header.column.getIsSorted()
                          return (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 select-none'
                            >
                              <div className='flex items-center gap-2'>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {sorted === false && <ArrowUpDown className='w-4 h-4 opacity-50' />}
                                {sorted === 'asc' && <ArrowUp className='w-4 h-4 text-blue-600' />}
                                {sorted === 'desc' && <ArrowDown className='w-4 h-4 text-blue-600' />}
                              </div>
                            </th>
                          )
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {table.getRowModel().rows.map((row: any) => (
                      <tr key={row.id} className='hover:bg-gray-50'>
                        {row.getVisibleCells().map((cell: any) => (
                          <td key={cell.id} className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <Modal
          dialogClassName='filter-modal-dialog w-1000px'
          show={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          centered
          backdrop='static'
        >
          <FilterModal
            key={location.pathname}
            filters={ReportsData[report as keyof typeof ReportsData]}
            data={filters}
            onApply={(newFilters: any) => {
              setFilters(newFilters)
              setShowFilterModal(false)
            }}
            onClose={() => setShowFilterModal(false)}
          />
        </Modal>

        <Modal
          id='modal_message'
          tabIndex={-1}
          dialogClassName='modal-dialog modal-dialog-centered mw-500px'
          contentClassName='bg-transparent border-0 shadow-none'
          backdropClassName='bg-black bg-opacity-70'
          show={downloadModal}
        >
          <DownloadModal
            url={downloadURL}
            jobId={jobId}
            error={downloadError}
            counter={totalCount}
            onClose={() => {
              showDownloadModal(false)
              setDownloadURL('')
            }}
          />
        </Modal>

        <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} centered size='xl'>
          <HelpModal
            reportName={report}
            pdfUrl={helpPdfUrl || undefined}
            fileName={helpFileName || undefined}
            onClose={() => setShowHelpModal(false)}
            onUploadSuccess={(pdfUrl) => {
              setHelpPdfUrl(pdfUrl)
              setHasPdf(true)
              setHelpFileName(extractFileNameFromUrl(pdfUrl))
              // garante que o status do botão reflita o estado real no backend
              checkManual()
            }}
          />
        </Modal>
      </div>
    </StyledWrapper>
  )
}

export default ReportSystem
